/**
 * @file useLegalTargets.ts
 * @description Документы и группы магазина для выбора цели ссылки (phase19.16,
 * партия «Юридические документы»; uiux-legal-steps-b-c §8): загрузка,
 * «Повторить» после ошибки и создание автоматической группы сертификатов
 * прямо из списка (решение 2026-10-19, §7.27 (д) п. 8).
 *
 * Список общий на вкладку (`legal-targets-store`): строки меню, поле цели и
 * подвал не шлют по запросу каждый. `fresh` — при открытии перечитать с
 * сервера (окно «Добавить ссылку на документ»). После создания и
 * «Повторить» список сбрасывается у всех и перечитывается (один источник
 * правды), а не дописывается руками.
 */

import { useCallback, useState } from "react";

import { useLegalTargetList } from "./useLegalTargetList";
import { useHasPermission } from "../../auth/hooks";
import { useNotify } from "../../components/ui/NotifyProvider";
import { ApiError } from "../../lib/api-client";
import { createCertificatesGroup, type LegalTarget } from "../../lib/api-legal-targets";
import { invalidateLegalTargets } from "../../lib/site-view/legal-targets-store";

export interface LegalTargetsApi {
  /** null — ещё грузится (или ошибка). */
  targets: LegalTarget[] | null;
  failed: boolean;
  reload: () => void;
  /** Право создавать автоматическую группу (настройки магазина). */
  canCreate: boolean;
  creating: boolean;
  /** Создать группу сертификатов; результат — группа или null при ошибке (toast уже показан). */
  createCertificates: () => Promise<LegalTarget | null>;
}

export function useLegalTargets({ fresh = false }: { fresh?: boolean } = {}): LegalTargetsApi {
  const canCreate = useHasPermission("SHOP_MANAGE_SETTINGS");
  const notify = useNotify();
  const { targets, failed } = useLegalTargetList({ fresh });
  const [creating, setCreating] = useState(false);

  const reload = useCallback(() => invalidateLegalTargets(), []);

  const createCertificates = useCallback(async () => {
    setCreating(true);
    try {
      const { target, published } = await createCertificatesGroup();
      if (published) notify.success(`Страница «${target.title}» создана и опубликована`);
      else notify.info(`Страница «${target.title}» создана — на сайте появится, когда у производителей будут сертификаты с файлом и сроком`);
      invalidateLegalTargets();
      return target;
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось создать страницу сертификатов. Попробуйте ещё раз.");
      return null;
    } finally {
      setCreating(false);
    }
  }, [notify]);

  return { targets, failed, reload, canCreate, creating, createCertificates };
}
